/**
 * Servicio para gestión de solicitudes de aprobación de clubes.
 *
 * Cuando un director registra un club nuevo, se crea una solicitud aquí.
 * El admin de asociación la aprueba o rechaza.
 * Si la aprueba, se asigna el rol club_director (sin auto-asignación).
 */

import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
  addDoc,
  Timestamp,
  limit,
} from "firebase/firestore";
import { db } from "@/lib/firebase";

/**
 * Elimina físicamente un club que nunca fue aprobado (sin miembros, sin
 * subcolecciones). Si el club tiene actividad, hace fallback a soft-delete
 * (status="inactive") para no perder datos.
 *
 * Bug #9: además, limpia roleAssignments y otras clubApprovalRequests
 * huérfanas para el mismo clubId. (Cloud Function `onClubDeleted` también
 * cascadea como defensa en profundidad.)
 */
async function deletePendingClubSafely(clubId: string): Promise<void> {
  const clubRef = doc(db, "clubs", clubId);
  // Comprobar miembros (colección top-level) antes de eliminar.
  const membersQ = query(
    collection(db, "members"),
    where("clubId", "==", clubId),
    limit(1),
  );
  const membersSnap = await getDocs(membersQ);
  if (!membersSnap.empty) {
    // Hay miembros → soft-delete para no romper integridad.
    await updateDoc(clubRef, {
      status: "inactive",
      updatedAt: serverTimestamp(),
    });
    return;
  }

  // Cleanup pre-delete: roleAssignments scopeType=club + scopeId=clubId
  const rolesSnap = await getDocs(
    query(
      collection(db, "roleAssignments"),
      where("scopeType", "==", "club"),
      where("scopeId", "==", clubId),
    ),
  );
  await Promise.all(rolesSnap.docs.map((d) => deleteDoc(d.ref)));

  await deleteDoc(clubRef);
}

// ============================================
// TIPOS
// ============================================

export type ClubApprovalStatus = "pending" | "approved" | "rejected";

export interface ClubApprovalRequest {
  id: string;
  /** ID del usuario que solicita ser director */
  userId: string;
  userEmail: string;
  userName: string;
  /** ID del club creado (en estado pending_approval) */
  clubId: string;
  clubName: string;
  /** ID de la asociación a la que pertenece */
  associationId: string;
  /** Datos personales del director */
  personalData: {
    firstName: string;
    lastName: string;
    phone: string;
    birthDate: Date;
    idDocumentType: string;
    idDocumentNumber: string;
    gender: string;
    bloodType: string;
    address: string;
    memberType: string;
    emergencyContact: {
      name: string;
      relationship: string;
      phone: string;
    };
  };
  /** Datos del club */
  clubData: {
    churchName?: string;
    churchId?: string;
    sections: Record<string, { active: boolean }>;
    inviteCode: string;
  };
  status: ClubApprovalStatus;
  rejectionReason?: string;
  processedBy?: string;
  processedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================
// HELPERS
// ============================================

const COLLECTION = "clubApprovalRequests";

function mapDoc(docSnap: {
  id: string;
  data: () => Record<string, unknown>;
}): ClubApprovalRequest {
  const d = docSnap.data();
  return {
    id: docSnap.id,
    ...d,
    personalData: {
      ...(d.personalData as Record<string, unknown>),
      birthDate:
        (d.personalData as Record<string, unknown>).birthDate instanceof
        Timestamp
          ? (
              (d.personalData as Record<string, unknown>).birthDate as Timestamp
            ).toDate()
          : ((d.personalData as Record<string, unknown>).birthDate as Date),
    },
    createdAt: (d.createdAt as Timestamp)?.toDate() || new Date(),
    updatedAt: (d.updatedAt as Timestamp)?.toDate() || new Date(),
    processedAt: d.processedAt
      ? (d.processedAt as Timestamp)?.toDate()
      : undefined,
  } as ClubApprovalRequest;
}

// ============================================
// QUERIES
// ============================================

/**
 * Obtiene la solicitud pendiente más reciente de un usuario.
 */
export async function getUserPendingRequest(
  userId: string,
): Promise<ClubApprovalRequest | null> {
  const q = query(
    collection(db, COLLECTION),
    where("userId", "==", userId),
    where("status", "==", "pending"),
    limit(1),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return mapDoc(snap.docs[0]);
}

/**
 * Stream de la solicitud más reciente de un usuario (pending o rejected).
 */
export function watchUserLatestRequest(
  userId: string,
  onData: (request: ClubApprovalRequest | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION),
    where("userId", "==", userId),
    orderBy("createdAt", "desc"),
    limit(1),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty) {
        onData(null);
        return;
      }
      const req = mapDoc(snapshot.docs[0]);
      // Si ya fue aprobada, reportar null para que el UI redirija
      if (req.status === "approved") {
        onData(null);
        return;
      }
      onData(req);
    },
    onError,
  );
}

/**
 * Obtiene todas las solicitudes pendientes de una asociación.
 */
export async function getAssociationPendingRequests(
  associationId: string,
): Promise<ClubApprovalRequest[]> {
  const q = query(
    collection(db, COLLECTION),
    where("associationId", "==", associationId),
    where("status", "==", "pending"),
    orderBy("createdAt", "asc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map(mapDoc);
}

/**
 * Stream de solicitudes pendientes de una asociación.
 */
export function watchAssociationPendingRequests(
  associationId: string,
  onData: (requests: ClubApprovalRequest[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION),
    where("associationId", "==", associationId),
    where("status", "==", "pending"),
    orderBy("createdAt", "asc"),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      onData(snapshot.docs.map(mapDoc));
    },
    onError,
  );
}

// ============================================
// MUTATIONS
// ============================================

/**
 * Crea una nueva solicitud de aprobación de club.
 */
export async function createClubApprovalRequest(
  data: Omit<
    ClubApprovalRequest,
    | "id"
    | "status"
    | "createdAt"
    | "updatedAt"
    | "processedBy"
    | "processedAt"
    | "rejectionReason"
  >,
): Promise<string> {
  // Verificar que no tenga otra solicitud pendiente
  const existing = await getUserPendingRequest(data.userId);
  if (existing) {
    throw new Error("Ya tienes una solicitud de club pendiente de aprobación.");
  }

  const docRef = await addDoc(collection(db, COLLECTION), {
    ...data,
    personalData: {
      ...data.personalData,
      birthDate:
        data.personalData.birthDate instanceof Date
          ? Timestamp.fromDate(data.personalData.birthDate)
          : data.personalData.birthDate,
    },
    status: "pending",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

/**
 * Aprueba una solicitud de club.
 * Llamada por el admin de asociación — asigna el rol club_director.
 */
export async function approveClubRequest(
  requestId: string,
  processedBy: string,
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const reqSnap = await getDoc(reqRef);
  if (!reqSnap.exists()) throw new Error("Solicitud no encontrada");

  const request = mapDoc(reqSnap);
  if (request.status !== "pending") {
    throw new Error("La solicitud ya fue procesada");
  }

  // 1. Importar dinámicamente para evitar dependencias circulares
  const { assignRole } = await import("./roles");
  const { updateDoc: updateClubDoc } = await import("firebase/firestore");

  // 2. Activar el club
  const clubRef = doc(db, "clubs", request.clubId);
  await updateClubDoc(clubRef, {
    status: "active",
    updatedAt: serverTimestamp(),
  });

  // 3. Asignar rol de director (processedBy es el admin, NO el director)
  await assignRole(
    {
      userId: request.userId,
      userEmail: request.userEmail,
      userName: request.userName,
      role: "club_director",
      scopeType: "club",
      scopeId: request.clubId,
      scopeName: request.clubName,
      personalData: {
        firstName: request.personalData.firstName,
        lastName: request.personalData.lastName,
        phone: request.personalData.phone,
        birthDate: request.personalData.birthDate,
        idDocumentType: request.personalData.idDocumentType,
        idDocumentNumber: request.personalData.idDocumentNumber,
        gender: request.personalData.gender,
        bloodType: request.personalData.bloodType,
        address: request.personalData.address,
        memberType: request.personalData.memberType,
        emergencyContact: request.personalData.emergencyContact,
      },
    },
    processedBy, // El ID del admin de asociación (assignedBy != userId)
  );

  // 4. Marcar solicitud como aprobada
  await updateDoc(reqRef, {
    status: "approved",
    processedBy,
    processedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Rechaza una solicitud de club.
 * Desactiva el club creado (soft delete).
 */
export async function rejectClubRequest(
  requestId: string,
  processedBy: string,
  reason: string,
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const reqSnap = await getDoc(reqRef);
  if (!reqSnap.exists()) throw new Error("Solicitud no encontrada");

  const request = mapDoc(reqSnap);
  if (request.status !== "pending") {
    throw new Error("La solicitud ya fue procesada");
  }

  // Eliminar el club huérfano (nunca fue aprobado). Si por algún motivo ya
  // tiene miembros, hace fallback a soft-delete.
  await deletePendingClubSafely(request.clubId);

  // Marcar solicitud como rechazada
  await updateDoc(reqRef, {
    status: "rejected",
    processedBy,
    rejectionReason: reason,
    processedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Cancela una solicitud pendiente (por el propio usuario).
 */
export async function cancelClubRequest(requestId: string): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const reqSnap = await getDoc(reqRef);
  if (!reqSnap.exists()) throw new Error("Solicitud no encontrada");

  const data = reqSnap.data();
  if (data.status !== "pending") {
    throw new Error("Solo se pueden cancelar solicitudes pendientes");
  }

  // Eliminar el club huérfano (con fallback a soft-delete si tiene miembros).
  await deletePendingClubSafely(data.clubId as string);

  await updateDoc(reqRef, {
    status: "rejected",
    rejectionReason: "Cancelada por el usuario",
    processedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// Exportar como objeto para compatibilidad con patrones existentes
export const clubApprovalService = {
  getUserPendingRequest,
  watchUserLatestRequest,
  getAssociationPendingRequests,
  watchAssociationPendingRequests,
  createClubApprovalRequest,
  approveClubRequest,
  rejectClubRequest,
  cancelClubRequest,
};
